import { CSSObject } from '@emotion/react'
import {
  Color,
  CornerRadius,
  FontWeight,
  gemVar,
  OppositeTheme,
  Spacing,
  ThemedColors,
  Transition,
  Typography,
} from '@opallabs/gem'
import { modernWithFallback } from '@opallabs/lapidary'
import type { StyleProps } from './types'

const height = Spacing.XS * 10

const rootSelector = 'kuiper-weekViewDayHeader'

export const selectors = {
  root: rootSelector,
  date: `${rootSelector}--date`,
  inMarketTrigger: `${rootSelector}--inMarket`,
}

export const inMarketPreview = {
  cursor: 'pointer',
} satisfies CSSObject

export const container: CSSObject = {
  color: gemVar(ThemedColors.TextPrimary),
  alignItems: 'center',
  blockSize: height,
  display: 'flex',
  flexGrow: 1,
  flexShrink: 1,
  justifyContent: 'center',
  position: 'relative',
}

export const dropTarget = ({
  isActive,
  isForbidden,
  theme,
}: StyleProps & {
  isActive: boolean
  isForbidden: boolean
}): CSSObject => ({
  backgroundColor: isForbidden
    ? Color[theme].Surface.R100
    : Color[OppositeTheme[theme]].Surface.N000,
  insetInlineStart: 0,
  opacity: isActive || isForbidden ? 0.08 : 0,
  position: 'absolute',
  insetBlockStart: height,
  inlineSize: '100%',
  zIndex: 1,
  ...Transition(['background-color', 'opacity']),
  // This is adapted from the SCSS version of the styles. It's always larger
  // than what's actually needed, as `<WeekViewDayHeader>` will never be at the
  // top edge of the viewport. The excess size shouldn't have a negative effect
  // on performance, though, as it's a simple `<div>`.
  ...modernWithFallback(
    [
      { blockSize: `calc(100vb - ${height}px)` },
      { blockSize: `calc(100vh - ${height}px)` },
    ],
    'height: 1vb'
  ),
})

export const inMarketTrigger: CSSObject = {
  background: gemVar(ThemedColors.Surface3),
  borderRadius: CornerRadius.Large,
  display: 'none',
  inlineSize: 'max-content',
  left: '50%',
  position: 'absolute',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  padding: `${Spacing.XS}px ${Spacing.M}px`,
}

export const text: CSSObject = {
  ...Typography.T300,
  display: 'flex',
  fontWeight: FontWeight.Bold,
  textAlign: 'center',
  textTransform: 'uppercase',
  position: 'relative',
  [`&:hover .${selectors.inMarketTrigger}`]: {
    display: 'block',
  },
  [`&:hover .${selectors.date}`]: {
    visibility: 'hidden',
  },
}
